/**
 * useToggleSet - shared expand/collapse Set state (DUP-29)
 *
 * Replaces the per-page boilerplate of:
 *   const [expanded, setExpanded] = useState<Set<string>>(new Set());
 *   const toggleX = (id) => {
 *     const next = new Set(expanded);
 *     if (next.has(id)) { next.delete(id); } else { next.add(id); }
 *     setExpanded(next);
 *   };
 *
 * Every update creates a new Set copy (never mutates in place), exactly like
 * the original page implementations, so React re-renders on every change.
 */

import { useCallback, useState } from 'react';

export interface ToggleSet<T> {
  /** The current Set - use directly for membership checks in render (e.g. set.has(id)) */
  set: Set<T>;
  /** Add the item if absent, remove it if present */
  toggle: (item: T) => void;
  /** Membership check bound to the current Set */
  has: (item: T) => boolean;
  /** Add the item (no-op result if already present, but still produces a new Set) */
  expand: (item: T) => void;
  /** Remove the item (no-op result if absent, but still produces a new Set) */
  collapse: (item: T) => void;
  /** Replace the whole Set with new Set(items) - empty when called without arguments */
  reset: (items?: Iterable<T>) => void;
}

export function useToggleSet<T = string>(initial?: Iterable<T>): ToggleSet<T> {
  const [set, setSet] = useState<Set<T>>(() => new Set(initial));

  const toggle = useCallback((item: T) => {
    setSet(prev => {
      const next = new Set(prev);
      if (next.has(item)) {
        next.delete(item);
      } else {
        next.add(item);
      }
      return next;
    });
  }, []);

  const expand = useCallback((item: T) => {
    setSet(prev => {
      const next = new Set(prev);
      next.add(item);
      return next;
    });
  }, []);

  const collapse = useCallback((item: T) => {
    setSet(prev => {
      const next = new Set(prev);
      next.delete(item);
      return next;
    });
  }, []);

  const reset = useCallback((items?: Iterable<T>) => {
    setSet(new Set(items));
  }, []);

  const has = useCallback((item: T) => set.has(item), [set]);

  return { set, toggle, has, expand, collapse, reset };
}
